import { alpha, Box, Button, Typography, useTheme } from "@mui/material";
import { FileCard } from "@pages:app/document-translation/components/FileCard";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdCheckCircle, MdDownload } from "react-icons/md";

import { SuccessCardProps } from "../types";
import { getDocumentTypeLabel, getFileKind } from "../utils/fileHelpers";
import { ConfettiEffect } from "./ConfettiEffect";

export const SuccessCard = ({
  translatedFile,
  onDownload,
  onTranslateAnother,
}: SuccessCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [showConfetti, setShowConfetti] = useState(true);
  const documentLabel = getDocumentTypeLabel(
    getFileKind(translatedFile.filename),
  );

  useEffect(() => {
    // Hide confetti after animation completes
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 4000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {/* Confetti Effect */}
      {showConfetti && <ConfettiEffect />}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 4,
          minHeight: 300,
        }}
      >
        {/* Animated Checkmark */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          {/* Background Circle */}
          <Box
            sx={{
              position: "absolute",
              width: 100,
              height: 100,
              borderRadius: "50%",
              backgroundColor: theme.palette.primary.main,
              opacity: 0.1,
              animation: "scaleIn 0.5s ease-out",
              "@keyframes scaleIn": {
                "0%": {
                  transform: "scale(0)",
                  opacity: 0,
                },
                "100%": {
                  transform: "scale(1)",
                  opacity: 0.1,
                },
              },
            }}
          />

          <MdCheckCircle
            size={80}
            style={{
              color: theme.palette.primary.main,
              animation:
                "checkmarkPop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
            }}
          />
        </Box>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: "28px",
            color: (theme) => theme.palette.text.primary,
            textAlign: "center",
            mb: 4,
            animation: "fadeInUp 0.5s ease-out 0.2s both",
            "@keyframes fadeInUp": {
              "0%": {
                opacity: 0,
                transform: "translateY(20px)",
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          }}
        >
          {t("translation.successCard.title")}
        </Typography>

        <FileCard
          filename={translatedFile.filename}
          size={translatedFile.size}
          language={translatedFile.language}
        />

        {/* Download Button */}
        <Button
          variant="contained"
          fullWidth
          onClick={onDownload}
          startIcon={<MdDownload size={20} />}
          sx={{
            maxWidth: 480,
            minHeight: "44px",
            background: theme.palette.primary.main,
            color: "#ffffff",
            fontSize: "16px",
            fontWeight: 600,
            padding: "14px 20px",
            borderRadius: "16px",
            textTransform: "none",
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
            mb: "16px",
            animation: "fadeInUp 0.5s ease-out 0.6s both",
            "&:hover": {
              background: theme.palette.primary.dark,
              transform: "translateY(-2px)",
              boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
            },
            "&:active": {
              transform: "translateY(0)",
            },
          }}
        >
          {t("translation.downloadAction", { type: t(documentLabel) })}
        </Button>

        {/* Translate Another Link */}
        <Typography
          onClick={onTranslateAnother}
          sx={{
            color: "#666468",
            fontSize: "14px",
            textDecoration: "underline",
            cursor: "pointer",
            textAlign: "center",
            animation: "fadeInUp 0.5s ease-out 0.7s both",
            "&:hover": {
              color: "#1D1D1D",
            },
          }}
        >
          {t("translation.successCard.translateAnother")}
        </Typography>
      </Box>

      {/* Additional CSS for checkmark animation */}
      <style>
        {`
          @keyframes checkmarkPop {
            0% {
              transform: scale(0) rotate(-45deg);
              opacity: 0;
            }
            50% {
              transform: scale(1.2) rotate(0deg);
            }
            100% {
              transform: scale(1) rotate(0deg);
              opacity: 1;
            }
          }
        `}
      </style>
    </>
  );
};
